const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const MAD_DAD_HERO_SELECTOR = '.mad-dad-hero';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

function applyMadDadHeroReducedMotion(root = document) {
  const heroes = root.querySelectorAll(MAD_DAD_HERO_SELECTOR);
  if (heroes.length === 0) return;

  heroes.forEach((hero) => {
    hero.classList.toggle('mad-dad-hero--reduced-motion', reducedMotionQuery.matches);
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

// Mad Dad Labz Phase 2 Animation Enhancements
const SCIENCE_PROOF_CLASSNAME = 'science-proof-metric';
const HOW_IT_FEELS_CLASSNAME = 'how-it-feels-quality-item';
const CTA_BAND_CLASSNAME = 'cta-band-button';
let phase2ReducedMotionStyleTag;

// Enhanced reduced motion detection and application
function applyPhase2ReducedMotion(root = document) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Apply reduced motion to new Phase 2 sections
  const collections = [
    root.querySelectorAll(`.${SCIENCE_PROOF_CLASSNAME}`),
    root.querySelectorAll(`.${HOW_IT_FEELS_CLASSNAME}`),
    root.querySelectorAll(`.${CTA_BAND_CLASSNAME}`),
  ];

  collections.forEach((nodeList) => {
    nodeList.forEach((element) => {
      element.classList.toggle('reduced-motion', prefersReducedMotion);
    });
  });

  if (prefersReducedMotion) {
    if (!phase2ReducedMotionStyleTag) {
      phase2ReducedMotionStyleTag = document.createElement('style');
      phase2ReducedMotionStyleTag.setAttribute('data-phase2-reduced-motion', 'true');
      phase2ReducedMotionStyleTag.textContent = `
        .reduced-motion:hover {
          transform: none !important;
        }
        .reduced-motion {
          transition: none !important;
        }
      `;
      document.head.appendChild(phase2ReducedMotionStyleTag);
    }
  } else if (phase2ReducedMotionStyleTag && phase2ReducedMotionStyleTag.parentNode) {
    phase2ReducedMotionStyleTag.parentNode.removeChild(phase2ReducedMotionStyleTag);
    phase2ReducedMotionStyleTag = null;
  }
}

// Phase 2 specific scroll animations with performance optimization
function initializePhase2ScrollAnimations() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  
  if (prefersReducedMotion) return;
  
  // Science proof metrics - staggered fade in
  const scienceMetrics = document.querySelectorAll(`.${SCIENCE_PROOF_CLASSNAME}`);
  if (scienceMetrics.length > 0) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('science-proof-metric--visible');
          }, index * 150); // 150ms stagger
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1
    });
    
    scienceMetrics.forEach(metric => observer.observe(metric));
  }
  
  // How it feels qualities - slide in from left
  const feelQualities = document.querySelectorAll(`.${HOW_IT_FEELS_CLASSNAME}`);
  if (feelQualities.length > 0) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('how-it-feels-quality-item--visible');
          }, index * 100); // 100ms stagger
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -30px 0px',
      threshold: 0.1
    });
    
    feelQualities.forEach(quality => observer.observe(quality));
  }
  
  // CTA band - fade in with scale
  const ctaBand = document.querySelector('.cta-band-content');
  if (ctaBand) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('cta-band-content--visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.2
    });
    
    observer.observe(ctaBand);
  }
}

// Listen for reduced motion preference changes
window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function(e) {
  applyPhase2ReducedMotion();
  if (e.matches) {
    // Remove all animation classes if motion is reduced
    document.querySelectorAll('.science-proof-metric--visible, .how-it-feels-quality-item--visible, .cta-band-content--visible').forEach(el => {
      el.classList.remove('science-proof-metric--visible', 'how-it-feels-quality-item--visible', 'cta-band-content--visible');
    });
  } else {
    // Re-initialize animations if motion is preferred
    initializePhase2ScrollAnimations();
  }
});

// Enhanced initialization for Phase 2
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeChaosGridAnimations();
  applyPhase2ReducedMotion();
  initializePhase2ScrollAnimations();
});

// Shopify design mode support for Phase 2 sections
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    applyPhase2ReducedMotion(event.target);
    initializePhase2ScrollAnimations();
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    applyPhase2ReducedMotion();
    initializePhase2ScrollAnimations();
  });
}

// Mad Dad Labz Chaos Grid Animation Extensions
const CHAOS_GRID_CLASSNAME = 'chaos-grid';
const CHAOS_REVEAL_CLASSNAME = 'chaos-reveal';

// Throttle function for performance
function throttle(func, limit = 16) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

// Staggered scroll reveal for chaos grid with mobile optimization
function initializeChaosGridAnimations() {
  // Check for reduced motion or mobile performance constraints
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const isLowEndDevice = window.matchMedia('(max-width: 749px) and (max-resolution: 1.5dppx)').matches;
  
  if (prefersReducedMotion || isLowEndDevice) {
    // Still show elements but without animation
    const chaosGrids = document.querySelectorAll(`.${CHAOS_GRID_CLASSNAME}`);
    chaosGrids.forEach(grid => {
      const items = grid.querySelectorAll('.grid__item');
      items.forEach(item => {
        item.classList.add(CHAOS_REVEAL_CLASSNAME);
      });
    });
    return;
  }

  const chaosGrids = document.querySelectorAll(`.${CHAOS_GRID_CLASSNAME}`);
  if (chaosGrids.length === 0) return;

  chaosGrids.forEach(grid => {
    const items = Array.from(grid.querySelectorAll('.grid__item'));
    if (items.length === 0) return;

    // Set up intersection observer for staggered reveals with mobile optimization
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const staggerDelay = isMobile ? 50 : 100; // Faster on mobile
    const maxItems = isMobile ? 12 : items.length; // Limit animations on mobile
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const item = entry.target;
          const index = items.indexOf(item);
          
          // Limit animations on mobile for performance
          if (isMobile && index >= maxItems) {
            item.classList.add(CHAOS_REVEAL_CLASSNAME);
            observer.unobserve(item);
            return;
          }
          
          // Add staggered delay based on position
          setTimeout(() => {
            item.classList.add(CHAOS_REVEAL_CLASSNAME);
          }, index * staggerDelay);
          
          observer.unobserve(item);
        }
      });
    }, {
      rootMargin: isMobile ? '0px 0px -50px 0px' : '0px 0px -100px 0px',
      threshold: isMobile ? 0.2 : 0.1
    });

    // Observe each grid item
    items.forEach((item, index) => {
      // Set animation order CSS custom property
      item.style.setProperty('--animation-order', index);
      observer.observe(item);
    });
  });
}

// Enhanced texture preview interactions
function initializeTexturePreviewEffects(root = document) {
  const texturePreviews = root?.querySelectorAll ? root.querySelectorAll('.texture-preview') : [];
  if (!texturePreviews || texturePreviews.length === 0) return;

  texturePreviews.forEach((preview) => {
    if (!preview) return;

    const lens = preview.querySelector('.texture-preview__lens');
    if (!lens) return;

    const previewRectCache = { width: 0, height: 0 };

    const positionLens = (event) => {
      const rect = preview.getBoundingClientRect();
      previewRectCache.width = rect.width;
      previewRectCache.height = rect.height;

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Clamp lens within preview bounds
      const lensHalfWidth = lens.offsetWidth / 2 || 40;
      const lensHalfHeight = lens.offsetHeight / 2 || 40;

      const clampedX = Math.min(Math.max(x, lensHalfWidth), rect.width - lensHalfWidth);
      const clampedY = Math.min(Math.max(y, lensHalfHeight), rect.height - lensHalfHeight);

      lens.style.left = `${clampedX - lensHalfWidth}px`;
      lens.style.top = `${clampedY - lensHalfHeight}px`;
    };

    // Mouse move effect for magnification lens
    preview.addEventListener('mousemove', (event) => {
      positionLens(event);
    });

    // Reset lens position on mouse leave
    preview.addEventListener('mouseleave', () => {
      lens.style.left = '50%';
      lens.style.top = '50%';
      lens.style.transform = 'translate(-50%, -50%) scale(0.5)';
    });

    // Show lens on mouse enter
    preview.addEventListener('mouseenter', () => {
      lens.style.transform = 'translate(-50%, -50%) scale(1)';
    });
  });
}

// Laboratory-style particle animation
function initializeLaboratoryParticles() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const chaosGrids = document.querySelectorAll(`.${CHAOS_GRID_CLASSNAME}`);
  
  chaosGrids.forEach(grid => {
    // Create particle container
    const particleContainer = document.createElement('div');
    particleContainer.className = 'laboratory-particles';
    particleContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: -1;
      overflow: hidden;
    `;
    
    grid.style.position = 'relative';
    grid.appendChild(particleContainer);

    // Create floating particles
    for (let i = 0; i < 5; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 4px;
        height: 4px;
        background: rgba(1, 219, 243, 0.3);
        border-radius: 50%;
        animation: particle-float ${8 + Math.random() * 4}s ease-in-out infinite;
        animation-delay: ${Math.random() * 2}s;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
      `;
      particleContainer.appendChild(particle);
    }
  });
}

// Scientific grid overlay effect
function initializeScientificGrid() {
  const chaosGrids = document.querySelectorAll(`.${CHAOS_GRID_CLASSNAME}`);
  
  chaosGrids.forEach(grid => {
    // Add scientific grid overlay
    const gridOverlay = document.createElement('div');
    gridOverlay.className = 'scientific-grid-overlay';
    gridOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: 
        linear-gradient(rgba(5, 28, 54, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(5, 28, 54, 0.03) 1px, transparent 1px);
      background-size: 20px 20px;
      pointer-events: none;
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    grid.style.position = 'relative';
    grid.appendChild(gridOverlay);

    // Show grid on hover
    grid.addEventListener('mouseenter', () => {
      gridOverlay.style.opacity = '1';
    });

    grid.addEventListener('mouseleave', () => {
      gridOverlay.style.opacity = '0';
    });
  });
}

// Initialize all Mad Dad Labz animations
function initializeMadDadLabzAnimations() {
  initializeChaosGridAnimations();
  initializeTexturePreviewEffects();
  initializeLaboratoryParticles();
  initializeScientificGrid();
}

// Update the main DOMContentLoaded event listener
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  applyMadDadHeroReducedMotion();
  initializeMadDadLabzAnimations();
});

// Handle Shopify theme editor events
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    applyMadDadHeroReducedMotion(event.target);
    // Re-initialize chaos grid animations for new sections
    setTimeout(() => {
      initializeMadDadLabzAnimations();
    }, 100);
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    applyMadDadHeroReducedMotion(document);
    setTimeout(() => {
      initializeMadDadLabzAnimations();
    }, 100);
  });
}

if (typeof reducedMotionQuery.addEventListener === 'function') {
  reducedMotionQuery.addEventListener('change', () => applyMadDadHeroReducedMotion());
} else if (typeof reducedMotionQuery.addListener === 'function') {
  reducedMotionQuery.addListener(() => applyMadDadHeroReducedMotion());
}

// Performance optimization: debounce resize events
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Reinitialize animations on significant resize to prevent layout shift and improve performance
    initializeMadDadLabzAnimations();
  }, 250);
});

// Mobile Performance Monitoring and Optimization
class MobilePerformanceOptimizer {
  constructor() {
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.isLowEndDevice = this.detectLowEndDevice();
    this.animationBudget = this.isMobile ? 8 : 16; // Max concurrent animations
    this.activeAnimations = 0;
    
    if (this.isMobile) {
      this.init();
    }
  }
  
  detectLowEndDevice() {
    // Detect low-end devices based on various factors
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const lowEndConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
    const lowEndMemory = navigator.deviceMemory && navigator.deviceMemory < 4;
    const lowEndCores = navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4;
    
    return lowEndConnection || lowEndMemory || lowEndCores;
  }
  
  init() {
    this.setupPerformanceMonitoring();
    this.optimizeExistingAnimations();
  }
  
  setupPerformanceMonitoring() {
    // Monitor frame rate and adjust animations accordingly
    let lastTime = performance.now();
    let frameCount = 0;
    let fps = 60;
    
    const measureFPS = (currentTime) => {
      frameCount++;
      if (currentTime - lastTime >= 1000) {
        fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        frameCount = 0;
        lastTime = currentTime;
        
        // Adjust animation complexity based on FPS
        if (fps < 30 && !this.isLowEndDevice) {
          this.enableLowEndMode();
        } else if (fps > 50 && this.isLowEndDevice) {
          this.disableLowEndMode();
        }
      }
      
      if (this.activeAnimations > 0) {
        requestAnimationFrame(measureFPS);
      }
    };
    
    // Start monitoring when animations begin
    document.addEventListener('animationstart', () => {
      this.activeAnimations++;
      if (this.activeAnimations === 1) {
        requestAnimationFrame(measureFPS);
      }
    });
    
    document.addEventListener('animationend', () => {
      this.activeAnimations = Math.max(0, this.activeAnimations - 1);
    });
  }
  
  enableLowEndMode() {
    if (this.isLowEndDevice) return;
    
    this.isLowEndDevice = true;
    document.body.classList.add('low-end-device');
    
    // Disable complex animations
    const style = document.createElement('style');
    style.id = 'low-end-optimizations';
    style.textContent = `
      .low-end-device .chaos-grid .grid__item {
        transition: opacity 0.2s ease !important;
        transform: none !important;
      }
      .low-end-device .laboratory-beaker {
        animation: none !important;
      }
      .low-end-device .chaos-grid::after {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  disableLowEndMode() {
    this.isLowEndDevice = false;
    document.body.classList.remove('low-end-device');
    
    const style = document.querySelector('#low-end-optimizations');
    if (style) {
      style.remove();
    }
  }
  
  optimizeExistingAnimations() {
    if (this.isLowEndDevice) {
      // Disable particle effects
      const particles = document.querySelectorAll('.laboratory-particles');
      particles.forEach(particle => particle.remove());
      
      // Simplify grid overlays
      const overlays = document.querySelectorAll('.scientific-grid-overlay');
      overlays.forEach(overlay => overlay.style.display = 'none');
    }
  }
  
  canStartAnimation() {
    return this.activeAnimations < this.animationBudget;
  }
}

// Initialize mobile performance optimizer
let mobileOptimizer;
if (window.matchMedia('(max-width: 749px)').matches) {
  mobileOptimizer = new MobilePerformanceOptimizer();
}

// Update existing animation functions to respect performance budget
const originalInitializeChaosGridAnimations = initializeChaosGridAnimations;
initializeChaosGridAnimations = function() {
  if (mobileOptimizer && !mobileOptimizer.canStartAnimation()) {
    // Skip animations if budget exceeded
    const chaosGrids = document.querySelectorAll(`.${CHAOS_GRID_CLASSNAME}`);
    chaosGrids.forEach(grid => {
      const items = grid.querySelectorAll('.grid__item');
      items.forEach(item => {
        item.classList.add(CHAOS_REVEAL_CLASSNAME);
      });
    });
    return;
  }
  
  originalInitializeChaosGridAnimations();
};